import { zodResolver } from '@hookform/resolvers/zod';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { useMapsGoogleConfigurationQuery } from 'api/queries/config';
import { ControlledField, LocationInputField } from 'remote/global';
import { hasReadOnlyPermission } from 'utils/helpers';
import { usePutOperatorsVehiclesMutation } from 'api/queries/operators';
import { Button } from '@mui/material';
import { VehicleDefaultLocationSchemaType, vehicleDefaultLocationSchema } from './types';
import { VehicleDtoSchema } from '../BookingFormFactory/types';

export function VehicleDefaultLocationFormFields({
  form,
  focusInitial,
}: {
  form: UseFormReturn<VehicleDefaultLocationSchemaType>;
  focusInitial?: boolean;
}) {
  const { control } = form;
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { data: googleConfig } = useMapsGoogleConfigurationQuery();
  const { permissions } = userInfo ?? {};
  const isLocationFormDisabled = hasReadOnlyPermission(permissions?.VEHICLE_LOCATION ?? '');

  return (
    <ControlledField
      control={control}
      name='defaultLocation'
      disabled={isLocationFormDisabled}
      render={(renderProps) => (
        <LocationInputField
          {...renderProps}
          googleConfig={googleConfig!}
          focusInitial={focusInitial}
          fieldProps={{
            disableClearable: true,
          }}
        />
      )}
    />
  );
}

function VehicleDefaultLocationForm({
  defaultValues,
  vehicle,
}: {
  defaultValues?: VehicleDefaultLocationSchemaType;
  vehicle: VehicleDtoSchema;
}) {
  const { t } = useTranslation();
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions } = userInfo ?? {};
  const formInstance = useForm<VehicleDefaultLocationSchemaType>({
    defaultValues: {
      defaultLocation: undefined,
      ...defaultValues,
    },
    resolver: zodResolver(vehicleDefaultLocationSchema),
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;
  const putOperatorsVehiclesMutation = usePutOperatorsVehiclesMutation();
  const isLocationFormDisabled = hasReadOnlyPermission(permissions?.VEHICLE_LOCATION ?? '');

  const onValid = async (data: VehicleDefaultLocationSchemaType) => {
    const payload: VehicleDtoSchema = {
      ...vehicle,
      ...(data as VehicleDtoSchema),
    };

    await putOperatorsVehiclesMutation.mutateAsync(payload);
  };

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <VehicleDefaultLocationFormFields form={formInstance} />
      <Button
        type='submit'
        variant='contained'
        disabled={isLoading || isSubmitting || isValidating || isLocationFormDisabled}
        fullWidth
        sx={{ mt: '2rem' }}
      >
        {t('save')}
      </Button>
    </form>
  );
}

export default VehicleDefaultLocationForm;
